import _ from 'lodash';

import FieldUtils from 'app/beta-editor/utils/FieldUtils';
import ruleMatchChoices from 'app/beta-editor/ruleMatchChoices';
import { getNewestCrumbForId } from 'app/beta-editor/utils/TaskStateUtils';
import {
  TEXT_CONTAINS,
  TEXT_DOES_NOT_END_WITH,
  TEXT_DOES_NOT_START_WITH,
  TEXT_ENDS_WITH,
  TEXT_STARTS_WITH,
} from 'app/beta-editor/constants/matchTypes';
import { createId, escapeRegExp } from 'app/common/CommonUtils';

export const FILTER_ICONS = {
  true: 'check-alt',
  false: 'stopped',
};

export const FAIL_STATUSES = ['fail', 'harmless-fail'];

const negator = 'not-';
const negatorRegex = /^not-/;

const ruleGroups = rules => _.groupBy(rules, rule => rule.group);

const hasNegationMatch = (rule = {}) => negatorRegex.test(rule.match);

const hasNegationAction = (rule = {}) => rule.action === 'stop';

const toUnnegatedMatch = match => (match || '').replace(negatorRegex, '');

// Prefix `match` with `not-` and prevent doubling up to `not-not-`
const toNegatedMatch = match => `${negator}${toUnnegatedMatch(match)}`;

const toUiMatch = rule =>
  hasNegationAction(rule)
    ? toNegatedMatch(rule.match)
    : toUnnegatedMatch(rule.match);

// `exist` and `boolean` type rules should not have a `value`
const toServerValue = rule => {
  const fieldsWithoutValues = [/exist/, /boolean/];
  // check that our rule is not 'exist' or 'boolean' type
  const results = fieldsWithoutValues.map(type =>
    type.test(toUnnegatedMatch(rule.match))
  );
  // if it tested false for both, return the value.
  return _.isEmpty(_.compact(results)) ? rule.value : null;
};

// In the UI, we treat rules as `match: not-icontains` for example,
// however the backend expects `match: icontains, action: stop`.
// This function handles that conversion, stripping `not-` from
// `match` and changing the `action` to the necessary value.
const toServerRule = rule => {
  return {
    ...rule,
    match: toUnnegatedMatch(rule.match),
    action: hasNegationMatch(rule) ? 'stop' : 'continue',
    value: toServerValue(rule),
  };
};

// Inverse of `toServerRule`; prefixes `match` with `not-`
// when necessary.
const toUiRule = rule => ({
  ...rule,
  action: null, // ignore `action` succinctly
  match: toUiMatch(rule),
});

const matchText = rule => {
  return ruleMatchChoices[toUiMatch(rule)];
};

const findResult = (results, filterId) =>
  _.find(results, result => result.id === filterId);

const filterSampleValue = (results, filterId) =>
  FieldUtils.formatSampleValue((findResult(results, filterId) || {}).sample);

const filterDidPass = (results, filterId) =>
  !!(findResult(results, filterId) || {}).did_pass;

const resultsById = filters =>
  _.reduce(
    filters,
    (result, filter) => ({ ...result, [filter.id]: filter }),
    {}
  );

const filterRulesResults = (rules, results, wouldWholeFilterRun) =>
  rules.map(rule => ({
    ...rule,
    sample: filterSampleValue(results, rule.id) || '(missing value)',
    match: matchText(rule) || '(missing comparison)',
    status: filterDidPass(results, rule.id)
      ? 'success'
      : wouldWholeFilterRun
      ? 'harmless-fail'
      : 'fail',
  }));

const groupWouldRun = group =>
  !_.some(group, groupItem => _.includes(FAIL_STATUSES, groupItem.status));

export const sampleCreateDate = (flow, nodeId) => {
  const crumb = getNewestCrumbForId(nodeId, flow.taskState) || {};
  return crumb.start_time;
};

export const filtersResult = (flow, filterNodeId) => {
  const node = flow.nodes[filterNodeId];
  const rules = node.params.filter_criteria || [];
  const crumb = getNewestCrumbForId(filterNodeId, flow.taskState) || {};
  const wouldRun = crumb.status === 'success';
  const results =
    _.get(flow, [
      'taskState',
      'output',
      filterNodeId,
      '_zap_data_filter_meta',
    ]) || [];
  return {
    wouldRun,
    rules,
    filters: filterRulesResults(rules, results, wouldRun),
  };
};

const resultByGroup = filters => _.groupBy(filters, 'group');

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

// Fix any string rule or group ids that may have been created from ZTs.
const fixRuleIds = (rules = []) => {
  const reducer = (key, result, rule) => {
    if (!(rule[key] in result)) {
      const numValue = parseInt(rule[key], 10);
      result[rule[key]] = isNaN(numValue) ? createId() : numValue;
    }
    return result;
  };
  const newRuleIds = rules.reduce(_.partial(reducer, 'id'), {});
  const newGroupIds = rules.reduce(_.partial(reducer, 'group'), {});
  return rules.map(rule => ({
    ...rule,
    id: newRuleIds[rule.id],
    group: newGroupIds[rule.group],
  }));
};

export const getMatchRegex = (method, value, sample) => {
  // these are the special cases -- end and beginning of strings.
  // otherwise, in most cases we can just match the whole sample.

  // we want to strip the surrounding parens in case the user has included those,
  // it will mess up the way the regex splits the sampmle.
  const escapedValue = escapeRegExp(value);
  return (
    {
      [TEXT_ENDS_WITH]: new RegExp(`${escapedValue}$`, 'gi'),
      // matching the negative here, so just match the last letter
      [TEXT_DOES_NOT_END_WITH]: new RegExp(`.$`, 'gi'),
      [TEXT_STARTS_WITH]: new RegExp(`^${escapedValue}`, 'gi'),
      // matching the negative here, so just match the first letter
      [TEXT_DOES_NOT_START_WITH]: new RegExp(`^.`, 'gi'),
      [TEXT_CONTAINS]: new RegExp(`${escapedValue}`, 'gi'),
    }[method] || escapeRegExp(sample)
  );
};

/*
  Split string into groups of matched and unmatched sections, like this:

  splitIntoMatchGroups('hello world', /world/g)

  result:
  [
    { isMatch: false, value: 'hello ' },
    { isMatch: true, value: 'world' }
  ]
*/

export const splitIntoMatchGroups = (string, re) => {
  if (!re.global) {
    throw new Error(
      'splitIntoMatchGroups only works when the global regexp flag is set!'
    );
  }

  let lastIndex = 0;
  const results = [];
  let m;

  while ((m = re.exec(string)) !== null) {
    results.push({
      isMatch: false,
      value: string.substring(lastIndex, m.index),
    });
    results.push({ isMatch: true, value: m[0] });
    lastIndex = re.lastIndex;
    if (re.lastIndex === m.index) {
      return results.filter(r => r.value);
    }
  }
  results.push({ isMatch: false, value: string.substring(lastIndex) });

  return results.filter(r => r.value);
};

export default {
  ruleGroups,
  filterRulesResults,
  filtersResult,
  toServerRule,
  toUiRule,
  toUiMatch,
  createId,
  fixRuleIds,
  FILTER_ICONS,
  resultsById,
  groupWouldRun,
  resultByGroup,
  sampleCreateDate,
  FAIL_STATUSES,
  getMatchRegex,
  splitIntoMatchGroups,
};
